import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from '../ui/Modal';
import { ArrowRight, ChevronDown, Info, Loader2, Terminal, X, Zap } from 'lucide-react';
import { useLanguage } from '../../i18n';
import { energyService } from '../../services/energyService';
import { logger } from '../../utils/logger';
import ContextThermostat from './ContextThermostat';

// ─────────────────────────────────────────────────────────────────────────
// EnergyReceiptModal — what THIS message cost (#349, #378, #376, #388).
//
// Replaces "View LLM Context" as the primary answer to "what happened here?":
// six lines of Tokens → Energy Points, a points-only total, and the share of
// the cycle's tank it consumed.
//
// THE TOTAL IS POINTS ONLY. Tokens are never summed across the lines — a
// transcription token, a cached input token and a thinking token are three
// different prices, so their sum is a number that means nothing. Points are the
// common unit, which is the entire reason points exist.
//
// Every points figure is printed VERBATIM from the backend's 2dp strings —
// never through parseFloat/toFixed. The rounding contract belongs to the server.
//
// #388 — CACHED vs ACTIVE. The backend has always billed cached input at a tenth
// of fresh, so the POINTS on this receipt were already right; what was missing
// was any way to SEE it, which made two structurally identical turns look
// arbitrarily priced (the PO's "147,116 tokens costing 98 points versus 150,000
// costing 27"). So every line now shows its cached/active split beside the
// counterfactual "N if 100% Active · Saved X%", and the header carries the turn's
// overall Cache Utilization Rate. The counterfactual is a HYPOTHETICAL and says
// so — the charged figure is the bold one.
//
// #378 — THE PER-STEP LINES. `input_context` and `system_overhead` are paid once
// per model call, because every call is a new request that must carry the whole
// context again. So both render their PER-STEP figure beside the step count —
// that, not the total, is what proves the thermostat's cap held on every step and
// what makes a 236,000-token overhead line read as "~40,528 re-sent twice"
// instead of one impossible payload. The copy deliberately says "re-sent each
// step" rather than presenting `per × steps` as an equation: the per-step figure
// is a measured estimate and the total is the authoritative billed number.
//
// PORTALED to document.body (the #225 pattern: the chat lives inside
// transformed panel ancestors that capture `position: fixed`).
// ─────────────────────────────────────────────────────────────────────────

// The EXACT sentence the card specifies for the (i) tooltip, in English. It is
// pinned byte-for-byte by `EnergyReceiptModal.test.jsx` and by the i18n contract
// test (`t(key, 'English')` must equal the `en` dictionary value), so it cannot
// drift through a reword.
export const CACHE_TOOLTIP_EN =
    'Cached input refers to information from your previous messages, documents, '
    + 'or system instructions that PAI has already processed. The system holds '
    + 'this information in an active state for a period of time so it does not '
    + 'have to be re-read from scratch. This is why cached information costs '
    + 'significantly fewer energy points in the final calculation.';

const LINES = {
    transcription: {
        label: ['receiptLineTranscription', 'Transcription'],
        hint: ['receiptLineTranscriptionHint', 'Audio and image input turned into text'],
    },
    input_context: {
        label: ['receiptLineInputContext', 'Input Context (Conversation History)'],
        hint: ['receiptLineInputContextHint', 'The conversation history PAI was holding'],
        tooltip: true,
    },
    memory_compression: {
        label: ['receiptLineMemoryCompression', 'Memory Compression'],
        hint: [
            'receiptLineMemoryCompressionHint',
            'Summarising the thread when it outgrew its memory budget',
        ],
        tooltip: true,
    },
    system_overhead: {
        label: ['receiptLineSystemOverhead', 'System Prompt'],
        hint: [
            'receiptLineSystemOverheadHint',
            'PAI’s instructions and tool definitions, re-sent on every step',
        ],
        tooltip: true,
    },
    reasoning: {
        label: ['receiptLineReasoning', 'PAI Reasoning & Output'],
        hint: ['receiptLineReasoningHint', 'Thinking, and generating the message text'],
    },
    tools: {
        label: ['receiptLineTools', 'Tools and Creation'],
        hint: ['receiptLineToolsHint', 'Web searches, documents, extraction'],
    },
    // #484 — NOT one of the six categories: the server sends it as its own
    // `prefix_cache` entry, only on a turn that set up (or kept alive) the
    // explicit prefix cache. It used to be folded into System Prompt, which
    // made a 39.6K prompt read 79K with "0 cached" on the turn that paid for
    // the cache. Same points, its own line.
    prefix_cache: {
        label: ['receiptLinePrefixCache', 'Prefix cache set-up'],
        hint: [
            'receiptLinePrefixCacheHint',
            'Saving PAI’s instructions once so the next messages re-send them for far less',
        ],
    },
};

const PARTS = {
    thinking: ['receiptPartThinking', 'PAI Thinking'],
    output: ['receiptPartOutput', 'PAI Output'],
    // #404 — the Transcription line's own sub-items. OCR was always charged
    // and debited (every `ocr` row carries a cost_usd and a matching ledger
    // entry) but it was invisible, which is why it was reported as not being
    // billed at all. A multi-page PDF writes one row per page and they sum
    // into this one figure.
    audio: ['receiptPartAudio', 'Audio Transcription'],
    ocr: ['receiptPartOcr', 'OCR / Image Processing'],
    // #454 — the Tools line's sub-items, sent only when the turn searched.
    // Google Search grounding is priced PER QUERY, not per token, so the fees
    // are their own part instead of hiding inside the call that ran them.
    tool_tokens: ['receiptPartToolTokens', 'Tool and creation tokens'],
    web_search_fees: ['receiptPartSearchFees', 'Web search fees'],
    // #499 — Google Places lookups for Maps links, priced per lookup.
    places_fees: ['receiptPartPlacesFees', 'Google Maps place lookups'],
    // #484 — the prefix-cache entry's parts.
    cache_create: ['receiptPartCacheCreate', 'Set-up (makes the next turns cheaper)'],
    cache_extend: ['receiptPartCacheExtend', 'Kept alive for the next turns'],
};

// #454 — a saving that rounds to 0% is still a saving, and "Saved 0%" next to a
// struck-through figure reads as a contradiction; "<1%" says what happened.
const savedText = (n) => (Number(n) < 1 ? '<1' : n);

// Per-query points for the fee part, derived from the part itself so the label
// can never disagree with the figure beside it.
const feeCount = (part) => Number((part.key === 'places_fees' ? part.lookups : part.searches) || 0);

const perSearch = (part) => {
    const searches = feeCount(part);
    if (!searches) return '0.00';
    return (Number(part.points || 0) / searches).toFixed(2);
};

const num = (value) => Number(value || 0).toLocaleString();

// #408 — what each main-model call in the turn actually WAS.
//
// The report was "3 visible blocks, billed 4 steps", read as a leak. Measured
// across 152 prod turns, 138 bill exactly `tools + 1`: after the last tool
// returns, the model must be called AGAIN to write the reply, and that call
// emits no tool chip — so the count can never match the chips, and a bare
// number invited the user to reconcile two things that legitimately differ.
//
// It is not all innocent, though, which is why this lists the steps instead of
// just explaining the +1: the #230 action-claim guard re-prompts when a reply
// claims an action no tool performed, and that is a whole extra ~40K-token
// call. Six prod turns in twelve days carry one. Those show as
// "Verification re-check" rather than padding the count anonymously.
const STEP_KIND_LABELS = {
    tool_iteration: ['receiptStepTool', 'Tool round'],
    final_answer: ['receiptStepFinal', 'Writing the answer'],
    verification_retry: ['receiptStepVerify', 'Verification re-check'],
    forced_synthesis: ['receiptStepSynthesis', 'Answering from what was gathered'],
    stream_anomaly: ['receiptStepAnomaly', 'Retry after a stream fault'],
    // #484 — prefix-cache rows are model-side calls too; listed (unnumbered)
    // so the count covers every row the turn billed.
    cache_create: ['receiptStepCacheCreate', 'Prefix cache set-up'],
    cache_extend: ['receiptStepCacheExtend', 'Prefix cache kept alive'],
};

// #484 — every listed call, including the grounded searches nested under a
// web_search step: "1 model call" on a turn that billed two rows was the
// second half of the report.
const countCalls = (steps) => steps.reduce(
    (n, step) => n + 1 + (Array.isArray(step.sub_steps) ? step.sub_steps.length : 0),
    0,
);

// #454 — the inner Gemini call a web_search tool makes, which fans out into
// several Google queries billed per query. Listed under the web_search step that
// ran it so the step list adds up to the turn.
// #499 — a Google Places lookup a read_link call made for a Maps link rides
// the same way under its read_link step.
const GroundedStep = ({ step, t, nested = false }) => (
    <li
        data-testid="receipt-step"
        data-kind={step.kind === 'places_lookup' ? 'places_lookup' : 'grounded_search'}
        data-searches={step.kind === 'places_lookup' ? undefined : Number(step.searches || 0)}
        className={`flex items-baseline justify-between gap-3 py-0.5 text-[12px] text-pai-meta min-w-0 ${nested ? 'pl-4' : ''}`}
    >
        <span className="min-w-0 break-words">
            {nested ? '↳ ' : ''}
            {step.kind === 'places_lookup'
                ? t('receiptStepPlaces', 'Google Maps place lookup')
                : t('receiptStepGrounded', 'Google Search ({n} queries)', { n: Number(step.searches || 0) })}
        </span>
        <span className="flex-shrink-0 tnum whitespace-nowrap">
            {t('receiptPoints', '{n} Energy Points', { n: step.points })}
        </span>
    </li>
);

const StepBreakdown = ({ steps, t }) => {
    const [open, setOpen] = useState(false);
    if (!Array.isArray(steps) || steps.length === 0) return null;
    const count = countCalls(steps);

    return (
        <div data-testid="receipt-steps" data-count={count} className="mt-3 min-w-0">
            <button
                type="button"
                data-testid="receipt-steps-toggle"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center justify-between gap-3 text-left min-w-0 text-pai-secondary hover:text-white transition-colors"
            >
                <span className="text-[12.5px] min-w-0 break-words">
                    {t('receiptStepsSummary', '{n} model calls in this turn', { n: count })}
                </span>
                <ChevronDown
                    size={14}
                    className={`flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
                />
            </button>
            {open && (
                <ol data-testid="receipt-steps-list" className="mt-2 min-w-0">
                    {steps.map((step, i) => {
                        if (step.kind === 'grounded_search' || step.kind === 'places_lookup') {
                            return <GroundedStep key={`g-${i}`} step={step} t={t} />;
                        }
                        const [kKey, kFallback] = STEP_KIND_LABELS[step.kind]
                            || [null, step.kind];
                        const subs = Array.isArray(step.sub_steps) ? step.sub_steps : [];
                        return (
                            <React.Fragment key={step.index ?? `s-${i}`}>
                                <li
                                    data-testid="receipt-step"
                                    data-kind={step.kind}
                                    data-tool={step.tool || undefined}
                                    className="flex items-baseline justify-between gap-3 py-0.5 text-[12px] text-pai-meta min-w-0"
                                >
                                    <span className="min-w-0 break-words">
                                        {step.index != null && `${step.index}. `}
                                        {step.tool || (kKey ? t(kKey, kFallback) : kFallback)}
                                    </span>
                                    <span className="flex-shrink-0 tnum whitespace-nowrap">
                                        {t('receiptPoints', '{n} Energy Points', { n: step.points })}
                                    </span>
                                </li>
                                {subs.map((sub, j) => (
                                    <GroundedStep key={`${step.index}-g-${j}`} step={sub} t={t} nested />
                                ))}
                            </React.Fragment>
                        );
                    })}
                    <li className="mt-1.5 pt-1.5 border-t border-pai-hairline text-[11.5px] text-pai-faint break-words">
                        {t(
                            'receiptStepsNote',
                            'Every tool round is one model call, and writing the final answer is one more — so a turn always bills one more call than the tools you see.',
                        )}
                    </li>
                </ol>
            )}
        </div>
    );
};

/**
 * The (i) affordance next to a cacheable line's label. A click-toggled popover
 * rather than a CSS :hover tooltip: the text is four sentences long, and a hover
 * tooltip is unreachable by keyboard and unreadable on touch.
 */
const CacheInfo = ({ t }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    return (
        <span ref={ref} className="relative inline-flex align-middle ml-1.5">
            <button
                type="button"
                data-testid="receipt-info-trigger"
                aria-expanded={open}
                aria-label={t('receiptCacheInfoLabel', 'What is cached input?')}
                onClick={() => setOpen((v) => !v)}
                className="text-pai-faint hover:text-white transition-colors"
            >
                <Info size={13} />
            </button>
            {open && (
                <span
                    data-testid="receipt-info-tooltip"
                    role="tooltip"
                    className="absolute left-0 top-full z-10 mt-2 w-[260px] rounded-card border border-pai bg-pai-modal p-3 text-[12px] leading-[1.45] text-pai-secondary shadow-modal"
                >
                    {t('receiptCacheInfo', CACHE_TOOLTIP_EN)}
                </span>
            )}
        </span>
    );
};

const Line = ({ entry, t }) => {
    const meta = LINES[entry.key] || {};
    const [labelKey, labelFallback] = meta.label || [null, entry.key];
    const [hintKey, hintFallback] = meta.hint || [];

    const total = Number(entry.tokens || 0);
    const cached = Number(entry.cached_tokens || 0);
    const active = Number(entry.active_tokens ?? total);
    const perCall = entry.per_call_tokens;
    const calls = Number(entry.calls || 0);
    // Only worth spelling out when the turn actually took several steps — on a
    // one-step turn the total already IS the per-step figure.
    const showPerCall = perCall !== undefined && perCall !== null && calls > 1;
    // A saving is only claimed when the server computed one. `saved_pct` is null
    // — never 0 — when this line had nothing cached, because "you saved 0%"
    // invites the reader to wonder whether caching broke.
    const saved = entry.saved_pct;
    const showSaved = saved !== null && saved !== undefined;
    const parts = Array.isArray(entry.parts) ? entry.parts : [];
    // #388 re-open — what Input Context is MADE of, per step: the history the
    // memory slider governs, and this turn's own message (plus the context that
    // rides on it), which it does not. Sent only when the server MEASURED it, so
    // its absence means "not measured", never "zero".
    const hasComposition = entry.history_tokens !== undefined
        && entry.history_tokens !== null
        && entry.turn_message_tokens !== undefined
        && entry.turn_message_tokens !== null;

    return (
        <li
            data-testid="receipt-line"
            data-category={entry.key}
            className="py-3 border-b border-pai-hairline last:border-0 min-w-0"
        >
            <span className="flex items-start justify-between gap-4 min-w-0">
                <span className="min-w-0">
                    <span className="block text-[14.5px] font-semibold text-white break-words">
                        {labelKey ? t(labelKey, labelFallback) : labelFallback}
                        {meta.tooltip && <CacheInfo t={t} />}
                    </span>
                    {hintKey && (
                        <span className="block text-[12px] text-pai-meta break-words mt-0.5">
                            {t(hintKey, hintFallback)}
                        </span>
                    )}
                    {/*
                      Left (label) column on purpose: it wraps. The right column is
                      flex-shrink-0, and a long string there is exactly the #406
                      bug that collapsed the label to one letter per line.
                    */}
                    {hasComposition && (
                        <span
                            data-testid="receipt-line-composition"
                            title={t('receiptCompositionHint', 'The memory slider limits history only — your new message always goes in whole.')}
                            className="block text-[11.5px] text-pai-meta break-words mt-1 tnum"
                        >
                            <span data-testid="receipt-history-tokens" data-tokens={Number(entry.history_tokens)}>
                                {t('receiptHistoryTokens', 'History {n}', { n: num(entry.history_tokens) })}
                            </span>
                            {' · '}
                            <span data-testid="receipt-message-tokens" data-tokens={Number(entry.turn_message_tokens)}>
                                {t('receiptMessageTokens', 'This message {n}', { n: num(entry.turn_message_tokens) })}
                            </span>
                        </span>
                    )}
                </span>
                <span className="flex-shrink-0 text-right tnum min-w-0">
                    <span className="flex items-center justify-end gap-2 whitespace-nowrap">
                        <span
                            data-testid="receipt-line-tokens"
                            data-tokens={total}
                            data-per-call={showPerCall ? perCall : undefined}
                            data-calls={showPerCall ? calls : undefined}
                            className="text-[13px] text-pai-secondary"
                        >
                            {t('receiptTokens', '{n} Tokens', { n: num(total) })}
                        </span>
                        <ArrowRight size={12} className="text-pai-faint flex-shrink-0" />
                        <span data-testid="receipt-line-points" className="text-[14px] font-bold text-white">
                            {t('receiptPoints', '{n} Energy Points', { n: entry.points })}
                        </span>
                    </span>
                    {/*
                      #406 — this sentence lives on its OWN line, and that is the
                      fix, not a preference. It used to be concatenated into the
                      tokens figure above, inside a `flex-shrink-0` column: a
                      ~45-character string then set the right column's
                      min-content width, the column refused to shrink, and the
                      only `min-w-0` child left to absorb the overflow was the
                      LABEL — which collapsed to one letter per line. It showed
                      up on exactly the two lines that carry `per_call_tokens`
                      (Input Context, System Prompt) and only when `calls > 1`,
                      which is precisely the "multi-step turn" in the report;
                      Memory Compression has no per-step figure, so it rendered
                      fine and made the bug look arbitrary.
                    */}
                    {showPerCall && (
                        <span
                            data-testid="receipt-line-per-step"
                            className="block text-[11.5px] text-pai-meta mt-0.5 break-words"
                        >
                            {t('receiptPerStepDetail', '{calls} steps × ~{per} re-sent each step', {
                                per: num(perCall),
                                calls,
                            })}
                        </span>
                    )}
                    {/*
                      Cache bookkeeping is rendered only where the server sent it
                      (#388 item 1): a generated-text or raw-media line has no
                      prompt prefix to cache, so "0 Cached" there read as a
                      caching failure rather than as a structural zero. The
                      server OMITS the fields on those lines, so `undefined` —
                      not 0 — is the signal.
                    */}
                    {total > 0 && entry.cached_tokens !== undefined && (
                        <span className="block text-[11.5px] text-pai-meta mt-1">
                            <span data-testid="receipt-line-cached" data-tokens={cached}>
                                {t('receiptCached', '{n} Cached', { n: num(cached) })}
                            </span>
                            {' · '}
                            <span data-testid="receipt-line-active" data-tokens={active}>
                                {t('receiptActive', '{n} Active', { n: num(active) })}
                            </span>
                        </span>
                    )}
                    {showSaved && (
                        <span className="block text-[11.5px] mt-0.5">
                            <span data-testid="receipt-line-uncached-cost" className="text-pai-faint line-through">
                                {t('receiptIfActive', '{n} if 100% Active', {
                                    n: entry.points_if_uncached,
                                })}
                            </span>
                            {' '}
                            <span data-testid="receipt-line-saved" data-saved={saved} className="text-pai-meta">
                                {t('receiptSaved', '· Saved {n}%', { n: savedText(saved) })}
                            </span>
                        </span>
                    )}
                </span>
            </span>
            {parts.length > 0 && (
                <span data-testid="receipt-line-parts" className="block mt-2 pl-3 border-l border-pai-hairline">
                    {parts.map((part) => {
                        const [pKey, pFallback] = PARTS[part.key] || [null, part.key];
                        const isFee = part.key === 'web_search_fees' || part.key === 'places_fees';
                        const isPlaces = part.key === 'places_fees';
                        return (
                            <span
                                key={part.key}
                                data-testid="receipt-part"
                                data-part={part.key}
                                data-searches={isFee && !isPlaces ? Number(part.searches || 0) : undefined}
                                data-lookups={isPlaces ? Number(part.lookups || 0) : undefined}
                                className="flex items-center justify-between gap-3 text-[12px] text-pai-meta min-w-0 py-0.5"
                            >
                                <span className="truncate">{pKey ? t(pKey, pFallback) : pFallback}</span>
                                {isFee ? (
                                    <span className="flex-shrink-0 tnum">
                                        <span data-testid="receipt-part-searches" data-searches={feeCount(part)}>
                                            {isPlaces
                                                ? t('receiptPlacesFeeDetail', '{n} place lookups × {rate}', {
                                                    n: feeCount(part),
                                                    rate: perSearch(part),
                                                })
                                                : t('receiptSearchFeeDetail', '{n} web searches × {rate}', {
                                                    n: Number(part.searches || 0),
                                                    rate: perSearch(part),
                                                })}
                                        </span>
                                        {' → '}
                                        <span data-testid="receipt-part-points" className="text-pai-secondary font-semibold">
                                            {part.points}
                                        </span>
                                    </span>
                                ) : (
                                <span className="flex-shrink-0 tnum">
                                    <span data-testid="receipt-part-tokens" data-tokens={Number(part.tokens || 0)}>
                                        {t('receiptTokens', '{n} Tokens', { n: num(part.tokens) })}
                                    </span>
                                    {' → '}
                                    <span data-testid="receipt-part-points" className="text-pai-secondary font-semibold">
                                        {part.points}
                                    </span>
                                </span>
                                )}
                            </span>
                        );
                    })}
                </span>
            )}
        </li>
    );
};

const EnergyReceiptModal = ({ conversationId, messageId, onClose, onOpenTechnical }) => {
    const { t } = useLanguage();
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await energyService.getTurnReceipt(conversationId, messageId);
            setData(res);
        } catch (err) {
            logger.warn('ENERGY', `receipt failed: ${err?.message || err}`);
            setError(true);
        }
    }, [conversationId, messageId]);

    useEffect(() => { load(); }, [load]);

    const applyBudget = async (tokens, { global }) => {
        setSaving(true);
        setSaveError(null);
        try {
            if (global) {
                await energyService.setGlobalContextBudget(tokens);
                // #537: the slider here shows THIS chat. A chat with its own
                // override kept it after "Set as Global Default", so nothing on
                // screen changed and the buttons stayed. The user just picked
                // this value for this chat too: drop the override so the chat
                // follows the new default (and the slider settles on it).
                if (conversationId) {
                    await energyService.setConversationContextBudget(conversationId, null);
                }
            } else {
                await energyService.setConversationContextBudget(conversationId, tokens);
            }
            // Re-read rather than patching locally: the server normalizes (snaps
            // and clamps) what it stores, so what it returns is the truth.
            await load();
            return true;
        } catch (err) {
            logger.warn('ENERGY', `context budget save failed: ${err?.message || err}`);
            setSaveError(t('thermostatSaveFailed', "Couldn't save that budget."));
            return false;
        } finally {
            setSaving(false);
        }
    };

    const budget = data?.context_budget;
    const cachePct = data?.cache_utilization_pct;
    const totalSaved = data?.total_saved_pct;

    // ⚠️ EXACTLY two levels: body > scrim > dialog (the GrayStackModal rule).
    // Escape is <Modal>'s (capture + stop): dismissing a receipt never tears
    // down the panel behind it (#272).
    return (
        <Modal
            onClose={onClose}
            zIndex="z-[70]"
            size="lg"
            ariaLabel={t('receiptTitle', 'Energy Receipt')}
            cardClassName="max-h-[85dvh] min-w-0"
            cardTestId="energy-receipt-modal"
        >
                <header className="flex items-start justify-between gap-3 px-8 pt-7 pb-4">
                    <span className="flex items-start gap-3 min-w-0">
                        <Zap size={18} className="text-pai-meta flex-shrink-0 mt-1" />
                        <span className="min-w-0">
                            <span className="block text-[19px] font-bold text-white tracking-[-0.015em] truncate">
                                {t('receiptTitle', 'Energy Receipt')}
                            </span>
                            <span className="block text-[13px] text-pai-meta break-words mt-1">
                                {t('receiptSubtitle', 'What this message cost you')}
                            </span>
                        </span>
                    </span>
                    <button
                        type="button"
                        data-testid="energy-receipt-close"
                        onClick={onClose}
                        aria-label={t('close', 'Close')}
                        className="pai-icon-btn w-[38px] h-[38px] flex-shrink-0"
                    >
                        <X size={18} />
                    </button>
                </header>

                <div className="px-8 pb-7 min-w-0">
                    {error ? (
                        <p data-testid="energy-receipt-error" className="py-8 text-center text-[13px] text-pai-meta">
                            {t('energyError', "Couldn't load your energy data.")}
                        </p>
                    ) : !data ? (
                        <p className="flex items-center justify-center gap-2 py-8 text-[13px] text-pai-meta">
                            <Loader2 size={14} className="animate-spin" />
                            {t('energyLoading', 'Loading…')}
                        </p>
                    ) : (
                        <>
                            {/* The global cache badge (#388 item 1) — the share of this
                                turn's INPUT tokens served from cache. Rendered even at
                                0.00% so its absence always means "no input at all",
                                never "we didn't measure it". */}
                            {cachePct !== null && cachePct !== undefined && (
                                <div
                                    data-testid="receipt-cache-badge"
                                    data-pct={cachePct}
                                    className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mb-4 px-3 py-2 rounded-card border border-pai-hairline bg-white/[0.03] min-w-0"
                                >
                                    <span className="text-[12.5px] font-semibold text-white tnum">
                                        {t('receiptCacheRate', 'Cache Utilization Rate: {n}%', { n: cachePct })}
                                    </span>
                                    <span className="text-[11.5px] text-pai-meta tnum break-words">
                                        {t('receiptCacheRateDetail', '{cached} of {total} input tokens served from cache', {
                                            cached: num(data.cached_input_tokens),
                                            total: num(data.total_input_tokens),
                                        })}
                                    </span>
                                </div>
                            )}

                            <ul className="min-w-0 border-t border-pai-hairline">
                                {(data.categories || []).map((entry) => (
                                    <Line key={entry.key} entry={entry} t={t} />
                                ))}
                                {data.prefix_cache && (
                                    <Line entry={{ ...data.prefix_cache, key: 'prefix_cache' }} t={t} />
                                )}
                            </ul>

                            <StepBreakdown steps={data.step_breakdown} t={t} />

                            {/* #446 Phantom turns: a run the user STOPPED before this
                                answer was still billed. Shown, never hidden — but kept
                                out of the lines and steps above, which describe the
                                answer the user actually got. */}
                            {(data.stopped_runs || []).map((run) => (
                                <div
                                    key={run.run_id}
                                    data-testid="receipt-stopped-run"
                                    data-label="stopped-message-cost"
                                    data-steps={run.steps}
                                    data-points={run.points}
                                    className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-pai-hairline min-w-0"
                                >
                                    {/* #457: the PO's wording — the energy the stopped
                                        message spent before the user interrupted it. */}
                                    <span className="text-[13px] text-pai-secondary min-w-0 break-words">
                                        <span className="block">{t('receiptStoppedMessageCost', 'Stopped message cost')}</span>
                                        <span className="block text-[11px] text-pai-meta">
                                            {t('receiptStoppedMessageSteps', '{n} steps before you stopped it', { n: run.steps })}
                                        </span>
                                    </span>
                                    <span className="text-[13px] font-semibold text-white whitespace-nowrap">
                                        {t('receiptPoints', '{n} Energy Points', { n: run.points })}
                                    </span>
                                </div>
                            ))}

                            <div
                                data-testid="receipt-total"
                                className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-pai min-w-0"
                            >
                                <span className="text-[15px] font-semibold text-pai-secondary truncate">
                                    {t('receiptTotal', 'Total Turn Cost')}
                                </span>
                                <span className="text-right flex-shrink-0">
                                    <span
                                        data-testid="receipt-total-points"
                                        className="block text-[19px] font-extrabold text-white tnum tracking-[-0.02em]"
                                    >
                                        {t('receiptPoints', '{n} Energy Points', { n: data.total_points })}
                                    </span>
                                    {totalSaved !== null && totalSaved !== undefined && (
                                        <span className="block text-[11.5px] mt-0.5">
                                            <span
                                                data-testid="receipt-total-uncached"
                                                className="text-pai-faint line-through tnum"
                                            >
                                                {t('receiptIfActive', '{n} if 100% Active', {
                                                    n: data.total_points_if_uncached,
                                                })}
                                            </span>
                                            {' '}
                                            <span
                                                data-testid="receipt-total-saved"
                                                data-saved={totalSaved}
                                                className="text-pai-meta tnum"
                                            >
                                                {t('receiptSaved', '· Saved {n}%', { n: savedText(totalSaved) })}
                                            </span>
                                        </span>
                                    )}
                                    <span
                                        data-testid="receipt-tank-pct"
                                        className="block text-[12.5px] text-pai-meta tnum mt-0.5"
                                    >
                                        {data.tank_pct === null || data.tank_pct === undefined
                                            ? '—'
                                            : t('receiptTankPct', '{n}% of your Energy Tank', { n: data.tank_pct })}
                                    </span>
                                </span>
                            </div>

                            {budget && (
                                <div className="mt-7 pt-6 border-t border-pai-hairline min-w-0">
                                    <ContextThermostat
                                        scope="chat"
                                        limits={budget.limits}
                                        value={budget.conversation?.tokens ?? null}
                                        effectiveTokens={budget.effective?.tokens}
                                        busy={saving}
                                        error={saveError}
                                        onApply={applyBudget}
                                    />
                                </div>
                            )}

                            {onOpenTechnical && (
                                <button
                                    type="button"
                                    data-testid="receipt-technical-details"
                                    onClick={onOpenTechnical}
                                    className="mt-6 inline-flex items-center gap-1.5 text-[12.5px] text-pai-meta hover:text-white transition-colors min-w-0 max-w-full"
                                >
                                    <Terminal size={13} className="flex-shrink-0" />
                                    <span className="truncate">
                                        {t('receiptTechnicalDetails', 'Technical details')}
                                    </span>
                                </button>
                            )}
                        </>
                    )}
                </div>
            </Modal>
    );
};

export default EnergyReceiptModal;
